import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, CreditCard, ChevronRight, ChevronLeft, Zap, Coffee, MapPin, Sparkles, RefreshCw } from "lucide-react";
import axios from "axios";
import { API } from "../utils/constants";

const RED = "#E82529";

const PricingPage = ({ onBack, userEmail, setUserEmail, onStart }) => {
  const [packages, setPackages] = useState([]);
  const [email, setEmail] = useState(userEmail || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const loadPackages = () => {
    setLoadError(false);
    axios.get(`${API}/packages`)
      .then(r => setPackages(r.data.packages || []))
      .catch(() => setLoadError(true));
  };

  useEffect(() => { loadPackages(); }, []);

  const handleBuy = async (packageId) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) { setError("Enter your email first"); return; }
    setIsLoading(true);
    setError("");
    localStorage.setItem("ju_email", trimmed);
    if (setUserEmail) setUserEmail(trimmed);
    try {
      const res = await axios.post(`${API}/payment/create-checkout`, {
        package_id: packageId,
        email: trimmed,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        // Pre-open in same window to avoid popup blockers
        window.location.assign(res.data.url);
      } else {
        setError("Could not create payment session. Please try again.");
        setIsLoading(false);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Try again.");
      setIsLoading(false);
    }
  };

  const features = {
    discover: ["3 AI-optimized itineraries", "Neighborhood clustering + smart routing", "PDF export + Google Maps links", "Hidden gems & pro tips included"],
    journey: ["Everything in Discover", "12 itineraries — try different styles until it clicks", "The sweet spot for most travelers"],
    unfolded: ["Everything in Journey", "40 itineraries", "Live companion mode:", "  Replan your whole trip anytime — new priorities, new itinerary in seconds", "  Day went off-script? Recalculate what you can still fit in"],
  };
  const subtitles = { discover: "Try it out", journey: "Plan with confidence", unfolded: "Take it with you" };

  const icons = { discover: MapPin, journey: Zap, unfolded: Sparkles };

  const discover = packages.find(p => p.id === "discover");
  const journey = packages.find(p => p.id === "journey");
  const unfolded = packages.find(p => p.id === "unfolded");
  const ordered = [discover, journey, unfolded].filter(Boolean);

  return (
    <div className="min-h-screen py-16 px-4" style={{ background: "var(--zen-bg)" }}>
      <div className="max-w-4xl mx-auto">
        <button onClick={onBack} className="flex items-center gap-1 text-sm mb-8 hover:opacity-70 transition-opacity" style={{ color: "var(--zen-sub)" }} data-testid="pricing-back-btn">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl mb-3" style={{ color: "var(--zen-text)" }}>
            Choose your pass
          </h1>
          <p className="text-base" style={{ color: "var(--zen-sub)" }}>
            One-time purchase. No subscription. Yours forever.
          </p>
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-xs font-semibold"
            style={{ background: "var(--zen-accent)", color: "#fff" }} data-testid="launch-badge">
            Launch pricing — limited time
          </div>
        </motion.div>

        {/* Email input */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="max-w-md mx-auto mb-10">
          <div className="flex gap-2">
            <input
              type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="jf-field flex-1"
              data-testid="pricing-email-input"
            />
          </div>
          {error && <p className="text-xs mt-2 text-center" style={{ color: "var(--cat-nightlife-solid)" }}>{error}</p>}
        </motion.div>

        {/* Load failure — don't leave the buyer staring at an empty page */}
        {ordered.length === 0 && (
          <div className="text-center py-12 mb-8">
            <p className="text-sm mb-4" style={{ color: "var(--zen-sub)" }}>
              {loadError ? "We couldn't load our plans just now." : "Loading plans…"}
            </p>
            {loadError && (
              <button onClick={loadPackages} className="zen-btn zen-btn-primary px-6 py-2.5 text-sm">
                Try again
              </button>
            )}
          </div>
        )}

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12 md:items-stretch md:pt-4">
          {ordered.map((pkg, idx) => {
            const Icon = icons[pkg.id] || MapPin;
            const isFeatured = pkg.id === "journey";
            const isSelected = selectedPkg === pkg.id;
            const tierFeatures = features[pkg.id] || [];

            return (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 * (idx + 1) }}
                onClick={() => setSelectedPkg(pkg.id)}
                className={`relative p-6 transition-all flex flex-col cursor-pointer ${isFeatured ? "md:-mt-3 md:mb-0" : ""}`}
                style={{
                  background: isSelected ? "var(--zen-s2)" : "var(--zen-s1)",
                  border: isSelected ? "2px solid var(--zen-accent)" : "1.5px solid var(--zen-border)",
                  borderRadius: 8,
                }}
                data-testid={`pricing-tier-${pkg.id}`}
              >
                {isFeatured && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] px-2.5 py-0.5 rounded-full font-semibold tracking-wide whitespace-nowrap"
                    style={{ background: "var(--zen-bg)", color: "var(--zen-sub)", border: "1px solid var(--zen-border)" }}>
                    Most popular
                  </div>
                )}

                <div className="flex items-center gap-2 mb-4 mt-1">
                  <div className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 2, background: "var(--zen-accent)", color: "#fff" }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-mono-label text-xs" style={{ color: "var(--zen-accent)", letterSpacing: "0.16em" }}>
                      {pkg.id === "discover" ? "Discover" : pkg.id === "journey" ? "Journey" : "Unfolded"}
                    </p>
                    <p className="text-[10px]" style={{ color: "var(--zen-dim)" }}>{subtitles[pkg.id] || ""}</p>
                  </div>
                </div>

                <div className="flex items-baseline gap-2 mb-1">
                  <span style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 800, fontSize: 30, color: "var(--zen-text)" }}>${pkg.amount} <span className="text-sm font-normal" style={{ color: "var(--zen-dim)" }}>USD</span></span>
                  <span className="text-sm line-through" style={{ color: "var(--zen-dim)" }}>${pkg.regular_price}</span>
                </div>
                <p className="font-mono-label mb-5" style={{ fontSize: 9, letterSpacing: "0.1em", color: "var(--zen-dim)", textTransform: "none" }}>one-time purchase</p>

                <div className="space-y-2.5 mb-6 flex-1">
                  {tierFeatures.map((f, i) => f.startsWith("  ") ? (
                    <div key={i} className="flex items-start gap-2 ml-5">
                      <span className="text-xs mt-0.5 shrink-0" style={{ color: "var(--zen-dim)" }}>›</span>
                      <span className="text-xs" style={{ color: "var(--zen-dim)" }}>{f.trim()}</span>
                    </div>
                  ) : (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--zen-accent)" }} />
                      <span className="text-xs" style={{ color: "var(--zen-sub)" }}>{f}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedPkg(pkg.id); handleBuy(pkg.id); }}
                  disabled={isLoading}
                  className="w-full py-3 text-sm font-semibold transition-all flex items-center justify-center gap-2 mt-auto hover:opacity-90 disabled:opacity-60"
                  style={isSelected
                    ? { background: RED, color: "#fff", border: "none", borderRadius: 2, cursor: "pointer" }
                    : { background: "transparent", border: "1px solid var(--zen-border-h)", color: "var(--zen-text)", borderRadius: 2, cursor: "pointer" }}
                  data-testid={`pricing-buy-${pkg.id}`}
                >
                  {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <>
                    <CreditCard className="w-4 h-4" /> Get {pkg.id === "discover" ? "Discover" : pkg.id === "journey" ? "Journey" : "Unfolded"}
                  </>}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* CTA bottom */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-center">
          <p className="text-sm mb-4" style={{ color: "var(--zen-sub)" }}>
            Not sure yet? Build your trip and see Day 1 in full before you decide.
          </p>
          <button onClick={onStart} className="zen-btn zen-btn-primary px-8 py-3 text-sm" data-testid="pricing-start-planning">
            Start planning <ChevronRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;
